/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { BANGKOK, emptyAddress, FormAddress, formatAddress } from './forms.model';
import { ThaiAddressBook, ThaiAddressService, ThaiPlace } from './thai-address';

/**
 * A Thai address: the house and street typed in, then the area picked —
 * either by typing any part of it (a subdistrict, district, province or
 * postcode) and choosing from what matches, or province → district →
 * subdistrict, which fills in the postcode. The postcode can still be changed,
 * since a few subdistricts have more than one.
 */
@Component({
  selector: 'app-form-address',
  standalone: true,
  imports: [CommonModule, FormsModule, MatAutocompleteModule, MatIconModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="aca-frmaddr" role="group" [attr.aria-labelledby]="labelId" (focusout)="left($event)">
      <label class="aca-frmaddr__label" [for]="inputId">{{ 'DMS.FORMS.ADDRESS_LINE' | translate }}</label>
      <input
        class="aca-frmfill__input"
        [id]="inputId"
        [attr.aria-invalid]="(invalid && !address.line.trim()) || null"
        [placeholder]="'DMS.FORMS.ADDRESS_LINE_HINT' | translate"
        autocomplete="street-address"
        maxlength="500"
        [ngModel]="address.line"
        (ngModelChange)="set({ line: $event })"
      />

      <div class="aca-frmaddr__find">
        <mat-icon aria-hidden="true">search</mat-icon>
        <input
          class="aca-frmaddr__search"
          [id]="inputId + '-find'"
          type="search"
          [attr.aria-label]="'DMS.FORMS.ADDRESS_FIND' | translate"
          [placeholder]="'DMS.FORMS.ADDRESS_FIND' | translate"
          autocomplete="off"
          [disabled]="!book()"
          [matAutocomplete]="places"
          [ngModel]="query()"
          (ngModelChange)="find($event)"
        />
        <mat-autocomplete #places="matAutocomplete" class="aca-frmaddr__panel" [displayWith]="blank" (optionSelected)="pick($event)">
          <mat-option *ngFor="let place of found()" [value]="place">
            <span class="aca-frmaddr__option">{{ placeText(place) }}</span>
          </mat-option>
          <mat-option *ngIf="query().trim().length > 1 && !found().length" disabled>{{ 'DMS.FORMS.ADDRESS_NONE' | translate }}</mat-option>
        </mat-autocomplete>
      </div>

      <div class="aca-frmaddr__grid">
        <div class="aca-frmaddr__part">
          <label class="aca-frmaddr__label" [for]="inputId + '-province'">{{ 'DMS.FORMS.ADDRESS_PROVINCE' | translate }}</label>
          <select
            class="aca-frmfill__input"
            [id]="inputId + '-province'"
            [attr.aria-invalid]="(invalid && !address.province) || null"
            [disabled]="!book()"
            [ngModel]="address.province"
            (ngModelChange)="set({ province: $event, district: '', subdistrict: '', postcode: '' })"
          >
            <option value="">{{ 'DMS.FORMS.CHOOSE' | translate }}</option>
            <option *ngFor="let province of book()?.provinces" [value]="province">{{ province }}</option>
          </select>
        </div>
        <div class="aca-frmaddr__part">
          <label class="aca-frmaddr__label" [for]="inputId + '-district'">{{ districtLabel() | translate }}</label>
          <select
            class="aca-frmfill__input"
            [id]="inputId + '-district'"
            [attr.aria-invalid]="(invalid && !address.district) || null"
            [disabled]="!address.province"
            [ngModel]="address.district"
            (ngModelChange)="set({ district: $event, subdistrict: '', postcode: '' })"
          >
            <option value="">{{ 'DMS.FORMS.CHOOSE' | translate }}</option>
            <option *ngFor="let district of districts()" [value]="district">{{ district }}</option>
          </select>
        </div>
        <div class="aca-frmaddr__part">
          <label class="aca-frmaddr__label" [for]="inputId + '-subdistrict'">{{ subdistrictLabel() | translate }}</label>
          <select
            class="aca-frmfill__input"
            [id]="inputId + '-subdistrict'"
            [attr.aria-invalid]="(invalid && !address.subdistrict) || null"
            [disabled]="!address.district"
            [ngModel]="address.subdistrict"
            (ngModelChange)="chooseSubdistrict($event)"
          >
            <option value="">{{ 'DMS.FORMS.CHOOSE' | translate }}</option>
            <option *ngFor="let place of subdistricts()" [value]="place.subdistrict">{{ place.subdistrict }}</option>
          </select>
        </div>
        <div class="aca-frmaddr__part">
          <label class="aca-frmaddr__label" [for]="inputId + '-postcode'">{{ 'DMS.FORMS.ADDRESS_POSTCODE' | translate }}</label>
          <input
            class="aca-frmfill__input"
            [id]="inputId + '-postcode'"
            [attr.aria-invalid]="(invalid && !postcodeOk()) || null"
            inputmode="numeric"
            maxlength="5"
            autocomplete="postal-code"
            [ngModel]="address.postcode"
            (ngModelChange)="set({ postcode: $event })"
          />
        </div>
      </div>

      <p class="aca-frmaddr__failed" *ngIf="failed()" role="alert">
        <mat-icon aria-hidden="true">cloud_off</mat-icon>{{ 'DMS.FORMS.ADDRESS_LOAD_FAILED' | translate }}
        <button type="button" (click)="load()">{{ 'DMS.FORMS.REFRESH' | translate }}</button>
      </p>
      <p class="aca-frmaddr__reads" *ngIf="complete()" aria-live="polite">
        <mat-icon aria-hidden="true">markunread_mailbox</mat-icon>{{ printed() }}
      </p>
    </div>
  `
})
export class FormAddressComponent implements OnInit {
  private readonly addresses = inject(ThaiAddressService);
  private readonly host = inject(ElementRef<HTMLElement>);

  @Input() inputId = 'address';
  @Input() labelId?: string;
  @Input() invalid = false;
  @Input() set value(value: FormAddress | null | undefined) {
    this.address = { ...emptyAddress(), ...(value && typeof value === 'object' ? value : {}) };
  }
  @Output() valueChange = new EventEmitter<FormAddress>();
  /** Focus has left the whole address: time to check it. */
  @Output() touched = new EventEmitter<void>();

  address: FormAddress = emptyAddress();
  readonly book = signal<ThaiAddressBook | null>(null);
  readonly failed = signal(false);
  readonly query = signal('');
  readonly found = signal<ThaiPlace[]>([]);

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.failed.set(false);
    this.addresses.book().subscribe({
      next: (book) => this.book.set(book),
      error: () => {
        this.addresses.forget();
        this.failed.set(true);
      }
    });
  }

  districts(): string[] {
    return this.book()?.districts(this.address.province) ?? [];
  }

  subdistricts(): ThaiPlace[] {
    return this.book()?.subdistricts(this.address.province, this.address.district) ?? [];
  }

  /** Bangkok has เขต and แขวง; everywhere else อำเภอ and ตำบล. */
  districtLabel(): string {
    return !this.address.province
      ? 'DMS.FORMS.ADDRESS_DISTRICT'
      : this.address.province === BANGKOK
        ? 'DMS.FORMS.ADDRESS_KHET'
        : 'DMS.FORMS.ADDRESS_AMPHOE';
  }

  subdistrictLabel(): string {
    return !this.address.province
      ? 'DMS.FORMS.ADDRESS_SUBDISTRICT'
      : this.address.province === BANGKOK
        ? 'DMS.FORMS.ADDRESS_KHWAENG'
        : 'DMS.FORMS.ADDRESS_TAMBON';
  }

  placeText(place: ThaiPlace): string {
    return formatAddress({ line: '', ...place });
  }

  /** What the search box shows once a place is chosen: nothing, ready for another search. */
  readonly blank = (): string => '';

  /** The box also hands over the place chosen, before pick() runs. */
  find(text: unknown): void {
    this.query.set(typeof text === 'string' ? text : '');
    this.found.set(this.book()?.search(this.query()) ?? []);
  }

  pick(event: MatAutocompleteSelectedEvent): void {
    const place = event.option.value as ThaiPlace;
    this.set({ province: place.province, district: place.district, subdistrict: place.subdistrict, postcode: place.postcode });
    this.query.set('');
    this.found.set([]);
  }

  chooseSubdistrict(subdistrict: string): void {
    const place = this.book()?.find(this.address.province, this.address.district, subdistrict);
    this.set({ subdistrict, postcode: place?.postcode ?? '' });
  }

  set(change: Partial<FormAddress>): void {
    this.address = { ...this.address, ...change };
    this.valueChange.emit(this.address);
  }

  postcodeOk(): boolean {
    return /^[1-9]\d{4}$/.test(this.address.postcode.trim());
  }

  complete(): boolean {
    return !!(this.address.line.trim() && this.address.subdistrict && this.address.district && this.address.province && this.postcodeOk());
  }

  printed(): string {
    return formatAddress(this.address);
  }

  /** Moving between its own boxes is not leaving; the suggestions open outside it, in the overlay. */
  left(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (next && (this.host.nativeElement.contains(next) || (next as Element).closest?.('.aca-frmaddr__panel'))) {
      return;
    }
    this.touched.emit();
  }
}
